import {
  createContext,
  useContext,
  ReactNode,
  useState,
  useEffect,
  useCallback,
} from "react";
import Backdrop from "@mui/material/Backdrop";
import { backDropStyles } from "./BackDrop.styles";
import { useLocation, useNavigate } from "react-router-dom";
import {
  billsPath,
  isOrderCheckoutPath,
  isOrderingPath,
  parseTableScope,
} from "@/routes/paths";
import { useVenuesDataStore } from "@/store/useVenuesDataStore";

interface BackdropContextProps {
  children: ReactNode;
}

interface BackdropState {
  openedBy: string[];
  removeOpenedBy: (openedByValue: string) => void;
  updateOpenedBy: (openedByValue: string) => void;
}

const BackdropStateContext = createContext<BackdropState | undefined>(
  undefined,
);

export function BackdropStateProvider({ children }: BackdropContextProps) {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const { pathname } = location;

  // Initialize state from sessionStorage
  const [openedBy, setOpenedBy] = useState<string[]>(() => {
    const stored = sessionStorage.getItem("openedBy");
    return stored ? JSON.parse(stored) : [];
  });

  useEffect(() => {
    sessionStorage.setItem("openedBy", JSON.stringify(openedBy));
  }, [openedBy]);

  const updateOpenedBy = useCallback((openedByValue: string) => {
    setOpenedBy((prev) => {
      if (!prev.includes(openedByValue)) {
        return [...prev, openedByValue];
      }
      return prev;
    });
  }, []);

  const removeOpenedBy = useCallback((openedByValue: string) => {
    setOpenedBy((prev) => prev.filter((value) => value !== openedByValue));
  }, []);

  useEffect(() => {
    if (!openedBy.includes("transparent-drawer")) {
      setOpenedBy([]);
    } else {
      if (openedBy.includes("menu")) {
        removeOpenedBy("menu");
      }
    }
  }, []);

  // Safety net for the burger menu, whose tag is otherwise only cleared by the
  // button that set it. Open the menu, navigate back, and HomePage's own state
  // resets (or it unmounts entirely) while the tag survives in context and in
  // sessionStorage — leaving the whole app dimmed with nothing able to clear
  // it short of a reload. The menu is page-local, so any navigation closes it.
  useEffect(() => {
    removeOpenedBy("menu");
  }, [pathname, removeOpenedBy]);

  useEffect(() => {
    setOpen(openedBy.length > 0);
  }, [openedBy]);

  useEffect(() => {
    if (
      pathname.includes("pay-your-bill") ||
      pathname.includes("split-the-bill") ||
      pathname.includes("payment-error") ||
      pathname.includes("leave-tip") ||
      isOrderCheckoutPath(pathname)
    ) {
      updateOpenedBy("transparent-drawer");
    } else {
      removeOpenedBy("transparent-drawer");
    }
  }, [pathname, updateOpenedBy]);

  const navigate = useNavigate();
  const { remainingAmount } = useVenuesDataStore();

  // A fully paid bill kicks the guest back to the bill summary.
  //
  // Two things this must not do. It must not fire during ordering: a guest
  // ordering at a table with no open bill sits at remainingAmount === 0 for the
  // whole checkout, and would be yanked away mid-payment. And it must read the
  // table off the pathname rather than useParams() — this provider is mounted
  // above the router in main.tsx, so it has no route params and the redirect
  // used to resolve to /restaurants/undefined/tables/undefined/bills.
  useEffect(() => {
    if (remainingAmount !== 0 || isOrderingPath(pathname)) return;

    const isPaymentPath =
      pathname.includes("pay-your-bill") ||
      pathname.includes("split-the-bill") ||
      pathname.includes("leave-tip") ||
      pathname.includes("confirm-and-pay") ||
      pathname.includes("checkout-fee") ||
      pathname.includes("extra-tip") ||
      pathname.includes("payment-error");
    if (!isPaymentPath) return;

    const scope = parseTableScope(pathname);
    if (!scope) return;

    setOpenedBy([]);
    navigate(billsPath(scope.restaurantId, scope.tableId));
  }, [remainingAmount, navigate, pathname]);

  const backdropOverDrawer =
    pathname.includes("payment-error") || pathname.includes("checkout-fee");

  return (
    <BackdropStateContext.Provider
      value={{ openedBy, updateOpenedBy, removeOpenedBy }}
    >
      {children}
      <Backdrop
        open={open}
        sx={{ ...backDropStyles, ...(backdropOverDrawer ? { zIndex: 2 } : {}) }}
      />
    </BackdropStateContext.Provider>
  );
}

export const useBackdropState = () => {
  const context = useContext(BackdropStateContext);
  if (context === undefined) {
    throw new Error(
      "useBackdropState must be used within a BackdropStateProvider",
    );
  }
  return context;
};
